import _ from 'lodash';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { withRouter } from 'react-router';

import withDataDependencies from '@nerdwallet/structured-content/with-data-dependencies';
import { unpackDataDependencyIds } from './helpers';

/**
 * HOC that wraps a component with sugar, spice, and everything.
 * @name withDataDependency
 *
 * @param {Component} DataDependentComponent - The component to wrap with data provider support.
 * @param {Function} dataDependencies - The component specified data dependencies (include location href for clientside fetch support).
 * @param {String} [componentDataPropAlias] - The prop alias under which we should expect data.
 * @return {Component} - The wrapped component.
 */
export default (
  DataDependentComponent,
  dataDependencies,
  componentDataPropAlias = 'data'
) => {
  /**
   * We expect the data provider to provide the ids as well as product data mapping.
   */
  const DataWrapper = (props) => {
    const { dataDependency, [componentDataPropAlias]: data, cardLimit } = props;

    let products = {};
    let requestedIds = [];
    const resultSizeLimit = cardLimit;
    products = _.get(data, 'value', {});
    requestedIds = unpackDataDependencyIds(dataDependency);

    return (
      <DataDependentComponent
        {...props}
        requestedIds={requestedIds}
        products={products}
        resultSizeLimit={resultSizeLimit}
      />
    );
  };

  DataWrapper.propTypes = {
    data: PropTypes.shape({
      loading: PropTypes.bool,
      error: PropTypes.object,
      value: PropTypes.oneOfType([
        // Standard
        PropTypes.object,
      ]),
    }),
    dataDependency: PropTypes.shape({
      data: PropTypes.shape({
        ids: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
      }),
      type: PropTypes.string,
      productPage: PropTypes.string,
    }),
  };

  DataWrapper.defaultProps = {
    data: {},
    dataDependency: {},
  };

  // Bake-in available route information and the dataDependency mapper.
  const WrappedComponent = _.flow(
    (Component) => withDataDependencies(Component, dataDependencies),
    withRouter
  )(DataWrapper);

  return hoistNonReactStatics(WrappedComponent, DataDependentComponent);
};
